import pl from 'pluralize';

import EntriesMigrationService from './EntriesMigrationService';
import {
  EntriesResult,
  MigrateRequest,
  UpdateFieldResult,
} from '~/models/Operations';
import { Logger } from '~/util/logger';
import { isNullOrUndefined, isString } from '~/util/typeguards';
import { tryParse, tryStringify } from '~/util/tryparse';

const validationError = (message: string) => {
  return new Error(message, { cause: 'validation' });
};

class UpdateFieldService extends EntriesMigrationService {
  fieldId: string;
  find: string;
  replace: string;

  constructor(payload: MigrateRequest, preview: boolean) {
    // // Limit all entry searches to just this contentTypeId
    // // N.B. a supplied zenql will trump this
    if (payload.updateField) {
      payload.query = {
        ...(payload.query || {}),
        // contentTypeIds: [payload.updateField.contentTypeId],
        useDelivery: true,
        versionStatus: 'latest',
      };

      payload.transformGuids = false;
    }
    super(payload, preview);
    this.opName = 'find and replace';
    this.fieldId = payload.updateField?.fieldId || '';
    this.find = payload.updateField?.find || '';
    this.replace = payload.updateField?.replace || '';
  }

  /**
   * Find a specific value in an entry field and replace it with another
   */
  UpdateFieldContent = async () => {
    await this.Initialise();

    // get source entries so we can add in our new content
    await this.GetEntries({ withDependents: false });
    await this.TransformEntries();

    // commit or preview?
    const entriesResult = await this.MigrateEntries();

    // prepare result
    const result = this.MapUpdateFieldResult(entriesResult);
    return result;
  };

  /**
   * Validate inputs and hydrate the internal Contensis repositories
   * TODO: Resolve each content type fieldId in source and destinationId in target repository
   */
  Initialise = async () => {
    const { replace, fieldId, find } = this;

    // validate inputs
    if (!fieldId) throw validationError(`A fieldId to update is required`);
    if (!find) throw validationError(`A phrase to find is required`);
    if (typeof replace === 'undefined')
      throw validationError(`A replacement phrase is required`);

    // hydrate the source/target repos with content models
    // required here so we don't rehydrate later during migration
    await this.HydrateContensisRepositories();
  };

  /**
   * Add the new field value to the source entries,
   * applying transformations to the field value
   * and/or applying any provided template to modify the value
   * overwrites any existing data in destination field
   */
  TransformEntries = async () => {
    // TODO: Resolve each content type fieldId in source and destinationId in target repository
    // if (!this.fields.source?.field) return;

    const { fieldId, source } = this;

    const len = source.entries.raw.length;
    let updates = 0;
    Logger.info(
      `Checking and making replacements in ${pl('entry', len, true)}`
    );

    for (const entry of source.entries.raw) {
      let sourceEntryField = source.context.entry.getFieldValue(entry, fieldId);

      // if the returned value is an object we will try to stringify it
      // and flag it so we can parse the stringified object back if we
      // have made any update to the field
      let hasBeenStringified = false;
      if (sourceEntryField && typeof sourceEntryField === 'object') {
        const stringified = tryStringify(sourceEntryField);
        if (stringified !== sourceEntryField) {
          sourceEntryField = stringified;
          hasBeenStringified = true;
        }
      }

      // if the returned value is not null or undefined
      // we will ensure sourceEntryField is a string and call
      // replaceAll method converting the find string to a RegExp
      const replacementEntryField =
        !isNullOrUndefined(sourceEntryField) && sourceEntryField !== '[]'
          ? `${sourceEntryField}`.replaceAll(
              new RegExp(this.find, 'g'),
              this.replace
            )
          : // If find string is provided as "null", "undefined" or "[]"
            // return the replace string to allow
            // updating fields that are null or empty
            ['null', 'undefined', '[]'].includes(this.find)
            ? this.replace
            : sourceEntryField;

      // If the replacement and source field values are different
      // update the field's value in the source entry
      if (replacementEntryField != sourceEntryField) {
        source.context.entry.updateFieldValue(
          entry,
          fieldId,
          hasBeenStringified && isString(replacementEntryField)
            ? tryParse(replacementEntryField)
            : replacementEntryField === 'null' && this.replace === 'null' // Look for replacement of "null" to allow setting fields to null
              ? null
              : replacementEntryField
        );
        updates++;
      }
    }

    Logger.info(
      `Made ${updates} replacements in ${pl('entry', len, true)}`,
      true
    );
  };

  /**
   * Add additional info regarding the copy operation to the entries migration result
   * @param entriesResult The result of the entries migration
   * @returns The result of the copy field operation
   */
  MapUpdateFieldResult = (entriesResult: EntriesResult) => {
    const result: UpdateFieldResult = {
      updateField: {
        projectId: this.source.projectId,
        fieldId: this.fieldId,
        find: this.find,
        replace: this.replace,
      },
      ...entriesResult,
    };
    return result;
  };
}

export default UpdateFieldService;
